// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here ever talks to a server; the AI recap/ask calls are in brain.ts and send only
// the small derived slice (see signals.rs / HealthSignals). The photo→calories
// estimate goes DEVICE-DIRECT to the vendor on the customer's own key (ipc.rs).
//
// Data model:
//   meal   { id, name, eaten_on, calories (ESTIMATE kcal), protein_g/carbs_g/fat_g
//            (optional estimates), source manual|photo|label|barcode, note }
//   weight { id, measured_on, weight_kg, note } — one per date, re-log overwrites

import { invoke } from "./tauri-web";

export type MealSource = "manual" | "photo" | "label" | "barcode";
/** The day has parts, and a meal belongs to one. "" = logged before slots existed, shown as "Other".
 *  These are STRUCTURE, not targets — the health fence forbids goals, so an empty slot is just an
 *  empty slot, never a prompt to eat. */
export type MealSlot = "breakfast" | "lunch" | "dinner" | "snack" | "";
export const MEAL_SLOTS: Exclude<MealSlot, "">[] = ["breakfast", "lunch", "dinner", "snack"];

export interface Meal {
  id: string;
  name: string;
  /** YYYY-MM-DD (user-local day the meal was eaten). */
  eaten_on: string;
  /** Calorie ESTIMATE in kcal — always presented as an estimate. */
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  source: MealSource;
  /** Part of the day this meal belongs to; "" for meals logged before slots existed. */
  slot: MealSlot;
  note: string;
  created_at: string;
  updated_at: string;
}

export interface NewMeal {
  name: string;
  eaten_on?: string;
  calories?: number;
  protein_g?: number | null;
  carbs_g?: number | null;
  fat_g?: number | null;
  source?: MealSource;
  slot?: MealSlot;
  note?: string;
}

export type MealPatch = Partial<{
  name: string;
  eaten_on: string;
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  source: MealSource;
  slot: MealSlot;
  note: string;
}>;

export interface WeightEntry {
  id: string;
  measured_on: string;
  weight_kg: number;
  note: string;
  created_at: string;
}

export interface NewWeight {
  measured_on?: string;
  weight_kg: number;
  note?: string;
}

// ── Derived signals (computed on-device in Rust; only this small slice is sent to the brain) ──
// Mirrors src-tauri/src/signals.rs::HealthSignals. Every field is a computed aggregate over the
// user's OWN log — totals, deltas, streaks, patterns — never per-meal rows or notes. All calorie
// figures are the user's logged ESTIMATES. Purely observational; nothing here is a target/advice.
export interface DayTotal {
  date: string;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  meal_count: number;
}

export interface WeekWindow {
  days_logged: number;
  total_calories: number;
  avg_calories_per_logged_day: number | null;
  avg_protein_g: number | null;
  avg_carbs_g: number | null;
  avg_fat_g: number | null;
  meal_count: number;
}

export interface WeightTrend {
  entry_count: number;
  latest_kg: number | null;
  latest_on: string | null;
  change_30d_kg: number | null;
  earliest_in_window_on: string | null;
}

export interface SourceMix {
  manual: number;
  photo: number;
  label: number;
  barcode: number;
}

/** One pre-baked, ready-to-say observational fact (computed on-device). `kind` groups it,
 *  `text` is a full sentence with the figure, `tone` tints the UI dot (NOT advice). */
export interface HeadlineFact {
  kind: string;
  text: string;
  tone: "good" | "watch" | "neutral";
}

export interface HealthSignals {
  schema: string;
  generated_for: string;
  total_meals: number;
  distinct_days_logged: number;
  first_logged_on: string | null;
  logging_streak_days: number;
  daily_totals_14d: DayTotal[];
  this_week: WeekWindow;
  prev_week: WeekWindow;
  week_over_week_calories_pct: number | null;
  highest_avg_weekday: string | null;
  lowest_avg_weekday: string | null;
  source_mix: SourceMix;
  weight: WeightTrend;
  headline_facts: HeadlineFact[];
}

// ── Device-direct photo→calories estimate (customer's OWN key; see ipc.rs) ──────
export interface MealEstimate {
  meal_name: string;
  calories_estimate: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  confidence: "high" | "medium" | "low";
}

export const store = {
  listMeals: (opts?: { limit?: number }) =>
    invoke<Meal[]>("list_meals", { limit: opts?.limit ?? 50000 }),
  createMeal: (input: NewMeal) => invoke<Meal>("create_meal", { input }),
  updateMeal: (id: string, patch: MealPatch) => invoke<void>("update_meal", { id, patch }),
  deleteMeal: (id: string) => invoke<void>("delete_meal", { id }),

  listWeights: (opts?: { limit?: number }) =>
    invoke<WeightEntry[]>("list_weights", { limit: opts?.limit ?? 50000 }),
  upsertWeight: (input: NewWeight) => invoke<WeightEntry>("upsert_weight", { input }),
  deleteWeight: (id: string) => invoke<void>("delete_weight", { id }),

  /** Compute the derived-signals object on-device. `today` = client local YYYY-MM-DD so
   *  day math aligns to the user's clock. */
  computeSignals: (today: string) => invoke<HealthSignals>("compute_signals", { today }),

  /** Persist a nutrition-label photo's bytes on-device → returns its path (for OCR). */
  saveLabelPhoto: (bytes: number[], ext: string) =>
    invoke<string>("save_label_photo", { bytes, ext }),
  /** On-device OCR (Apple Vision) of a saved label photo → recognized text. */
  ocrImage: (path: string) => invoke<string>("ocr_image", { path }),
  /** On-device barcode detection (Apple Vision) of a saved photo → digit string. */
  detectBarcode: (path: string) => invoke<string>("detect_barcode", { path }),

  // §5d 2026-07-27: visionKeyPresent / visionKeySet / fetchAccountVisionKey /
  // analyzeMealPhoto removed with the device-direct vendor call they served. The photo
  // path is brain.ts::estimateMealPhoto -> /estimate-photo, where the router picks the
  // model. The MealEstimate type above stays — that IS the shape the brain route returns.
};

// ── Pure policy / display helpers ──────────────────────────────────────────────

export const SOURCE_LABELS: Record<MealSource, string> = {
  manual: "Typed",
  photo: "Photo",
  label: "Label scan",
  barcode: "Barcode",
};

export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Human "x days ago" for a YYYY-MM-DD date, relative to local today. */
export function relativeDays(dateStr: string | null): string {
  if (!dateStr) return "never";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return "never";
  const then = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const days = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - then.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.round(days / 7)} wk ago`;
  if (days < 365) return `${Math.round(days / 30)} mo ago`;
  return `${Math.round(days / 365)} yr ago`;
}

/** "2026-07-02" → "Wed, Jul 2" (local; adds the year when not current). */
export function prettyDate(dateStr: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return dateStr;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const opts: Intl.DateTimeFormatOptions =
    d.getFullYear() === new Date().getFullYear()
      ? { weekday: "short", month: "short", day: "numeric" }
      : { month: "short", day: "numeric", year: "numeric" };
  return d.toLocaleDateString(undefined, opts);
}

/** Parse a non-negative number from a text field ("" / invalid → null). */
export function parseNum(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const v = Number(t.replace(",", "."));
  return Number.isFinite(v) && v >= 0 ? v : null;
}
